<script setup>
import { computed, ref } from "vue";
import { useStore } from "vuex";
import Swal from 'sweetalert2';
import { useRouter, useRoute } from 'vue-router';
import Breadcrumbs from "../Breadcrumbs.vue";
import api from '@/api';
import { isAdmin as isAdminHelper } from "@/composables/useAuth";

const store = useStore();
const route = useRoute();
const router = useRouter();
const showMenu = ref(false);

// Input y rutas disponibles
const comando = ref('');
const rutasDisponibles = [
  { nombre: 'asistencias', ruta: '/asistencias' },
  { nombre: 'socios', ruta: '/users' },
  { nombre: 'membresias', ruta: '/membres' },
  { nombre: 'dashboard', ruta: '/dashboard-default' }
];

// Sugerencias según lo escrito
const sugerencias = computed(() => {
  const texto = comando.value.trim().toLowerCase();
  return texto
    ? rutasDisponibles.filter(r => r.nombre.includes(texto))
    : [];
});

const seleccionarSugerencia = (ruta) => {
  router.push(ruta);
  comando.value = '';
};

const isAdmin = computed(() => isAdminHelper());


const ejecutarComando = () => {
  const texto = comando.value.trim().toLowerCase();
  const coincidencia = rutasDisponibles.find(r => r.nombre === texto);
  if (coincidencia) {
    router.push(coincidencia.ruta);
  } else {
    Swal.fire('Ruta no reconocida', `No existe la vista "${texto}"`, 'warning');
  }
  comando.value = '';
};

const confirmLogout = () => {
  Swal.fire({
    title: '¿Cerrar sesión?',
    text: '¿Estás seguro de que quieres cerrar tu sesión?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Sí, salir',
    cancelButtonText: 'Cancelar',
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          await api.post('/auth/logout', { refreshToken });
        }
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.error('Error al cerrar sesión:', e);
      } finally {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('isLogged');
        router.push('/signin');
        Swal.fire('Sesión cerrada', 'Has cerrado sesión exitosamente', 'success');
      }
    }
  });
};

const minimizeSidebar = () => store.commit("sidebarMinimize");
const toggleConfigurator = () => store.commit("toggleConfigurator");
const closeMenu = () => setTimeout(() => (showMenu.value = false), 100);

const currentRouteName = computed(() => route.name);
const currentDirectory = computed(() => {
  let dir = route.path.split("/")[1];
  return dir.charAt(0).toUpperCase() + dir.slice(1);
});
</script>

<template>
  <nav class="navbar navbar-main navbar-expand-lg px-0 mx-4 shadow-none border-radius-xl" :class="''" v-bind="$attrs"
    id="navbarBlur" data-scroll="true">
    <div class="px-3 py-1 container-fluid">
      <breadcrumbs :current-page="currentRouteName" :current-directory="currentDirectory" />

      <div class="mt-2 collapse navbar-collapse mt-sm-0 me-md-0 me-sm-4" id="navbar">
        <!-- Autocomplete input -->
        <div class="pe-md-3 d-flex align-items-center ms-md-auto position-relative w-100">
          <div class="input-group w-100">
            <span class="input-group-text text-body">
              <i class="fas fa-search" aria-hidden="true"></i>
            </span>
            <input type="text" class="form-control" v-model="comando" :placeholder="'Buscar pantalla'"
              :disabled="!isAdmin" @keyup.enter="isAdmin && ejecutarComando()" />
          </div>
          <!-- Sugerencias -->
          <ul v-if="isAdmin && sugerencias.length" class="list-group position-absolute mt-1 w-100 z-index-10"
            style="top: 100%; max-height: 200px; overflow-y: auto;">
            <li v-for="s in sugerencias" :key="s.ruta" class="list-group-item list-group-item-action cursor-pointer"
              @click="seleccionarSugerencia(s.ruta)">
              {{ s.nombre }}
            </li>
          </ul>
        </div>

        <ul class="navbar-nav justify-content-end">
          <li class="nav-item d-flex align-items-center">
            <a href="#" @click="confirmLogout" class="nav-link d-flex align-items-center text-white px-2">
              <i class="fa fa-user me-2"></i>
              <span>Salir</span>
            </a>
          </li>
          <li class="nav-item d-xl-none ps-3 d-flex align-items-center">
            <a href="#" @click="minimizeSidebar" class="p-0 nav-link text-white" id="iconNavbarSidenav">
              <div class="sidenav-toggler-inner">
                <i class="sidenav-toggler-line bg-white"></i>
                <i class="sidenav-toggler-line bg-white"></i>
                <i class="sidenav-toggler-line bg-white"></i>
              </div>
            </a>
          </li>
          <li class="px-3 nav-item d-flex align-items-center">
            <a class="p-0 nav-link text-white" @click="toggleConfigurator">
              <i class="cursor-pointer fa fa-cog fixed-plugin-button-nav"></i>
            </a>
          </li>
          <li class="nav-item dropdown d-flex align-items-center pe-2">
            <a href="#" class="p-0 nav-link text-white" :class="[showMenu ? 'show' : '']" id="dropdownMenuButton"
              data-bs-toggle="dropdown" aria-expanded="false" @click="showMenu = !showMenu" @blur="closeMenu">
              <i class="cursor-pointer fa fa-bell"></i>
            </a>
            <ul class="px-2 py-3 dropdown-menu dropdown-menu-end me-sm-n4" :class="showMenu ? 'show' : ''"
              aria-labelledby="dropdownMenuButton">
            </ul>
          </li>
        </ul>
      </div>
    </div>
  </nav>
</template>

<style scoped>
.z-index-10 {
  z-index: 10;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
